import { GridEvents, useGridRootProps } from "@mui/x-data-grid-premium";
import { GridApiPremium, GridPrivateApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, useEffect, useRef } from "react";
import { DataGridProps } from "../models/dataGridProps";

export const useSaveGridState = (apiRef: MutableRefObject<GridPrivateApiPremium>) => {
  const rootProps = useGridRootProps() as DataGridProps;

  if (!rootProps.storageKey) {
        return;
    }

    const initialized = useRef(false);
    const storageKey = `${rootProps.storageKey}-state`;

    useEffect(() => {
        const ref = apiRef.current;

        if (!ref?.subscribeEvent) {
            return;
        }

        const restore = () => {
            const stateStr = localStorage.getItem(storageKey);
            if (stateStr) {
                const state = parseOrNull(stateStr);
                if (state) {
                    // NOTE: there appears to be a race condition where the column dimensions
                    // for the pinned columns are overwritten by the grid initialization.
                    // A timeout here did not work. You can observe the columns being resized
                    // to the custom width, and then they are resized back to the pinned column width
                    // after the row data has been loaded.
                    try {
                        const curState = ref.exportState();
                        const newState = {
                            ...curState,
                            columns: {
                                ...curState.columns,
                                ...state.columns,
                            },
                            density: state.density,
                        };
                        ref.restoreState(newState);
                    }
                    catch (e) {
                        console.warn(`Failed to restore grid state`, e);
                    }
                }
            }
        }

        const save = (event: any) => {
          console.log(event);
            const state = ref.exportState();
            if (state) {
                const gridState = {
                    columns: {
                        ...state.columns,
                    },
                    density: state.density,
                }
                localStorage.setItem(storageKey, JSON.stringify(gridState));
            }
        }

        const subscribeForSave = <E extends GridEvents>(event: E) => {
            subs.push(ref.subscribeEvent(event, save));
        }

        const subscribeForRestore = <E extends GridEvents>(event: E) => {
            subs.push(ref.subscribeEvent(event, restore));
        }

        // Restore state on first ref load
        if (!initialized.current) {
            initialized.current = true;
            restore();
        }

        const subs: VoidFunction[] = [];

        subscribeForRestore('rowsSet');

        subscribeForSave('columnResizeStop');
        subscribeForSave('columnOrderChange');
        subscribeForSave('pinnedColumnsChange');
        subscribeForSave('columnVisibilityModelChange');

        return () => {
            subs.forEach(unsub => {
                unsub()
            });
        }
    }, [apiRef]);
}

const parseOrNull = (obj: unknown) => {
    if (!obj) {
        return null;
    }

    if (typeof obj === 'string') {
        try {
            return JSON.parse(obj);
        } catch (e) {
            console.warn(`Failed to parse: ${obj.substring(0, 50)}`);
            return null
        }
    }

    return null;
}